import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, AlertCircle, Key, Link, Lightbulb, ChevronDown, Info, FileJson } from 'lucide-react';
import RelationshipImportExport from './RelationshipImportExport';

// Custom Table-Column Selector Component
const TableColumnSelector = ({ 
  value, 
  onChange, 
  availableTables, 
  placeholder = "Select table.column",
  disabled = false,
  showSuggestion = false,
  suggestionText = ""
}) => {
  // Parse current value to get table and column
  const [selectedTable, selectedColumn] = value ? value.split('.') : ['', ''];
  const [selectedTableName, setSelectedTableName] = useState(selectedTable);
  
  // Find the selected table object
  const tableObj = availableTables.find(t => t.name === selectedTableName);
  const availableColumns = tableObj?.schema?.map(field => field.name) || [];

  // Custom select styling
  const selectClassName = "w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:border-primary bg-white text-gray-700 appearance-none cursor-pointer hover:border-gray-400 transition-colors bg-no-repeat";
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.5rem center',
    backgroundSize: '1.5em 1.5em'
  };

  const handleTableChange = (tableName) => {
    setSelectedTableName(tableName);
    // Clear the full selection when table changes
    onChange('');
  };

  const handleColumnChange = (columnName) => {
    if (selectedTableName && columnName) {
      onChange(`${selectedTableName}.${columnName}`);
    } else {
      onChange('');
    }
  };

  return (
    <div className="space-y-2">
      {/* Table Selection */}
      <div>
        <select
          value={selectedTableName}
          onChange={(e) => handleTableChange(e.target.value)}
          disabled={disabled || availableTables.length === 0}
          className={selectClassName}
          style={selectStyle}
        >
          <option value="">Select table first</option>
          {availableTables.map(table => (
            <option key={table.id} value={table.name}>
              {table.name} ({table.schema?.length || 0} columns)
            </option>
          ))}
        </select>
      </div>

      {/* Column Selection */}
      <div>
        <select
          value={selectedColumn}
          onChange={(e) => handleColumnChange(e.target.value)}
          disabled={disabled || !selectedTableName || availableColumns.length === 0}
          className={`${selectClassName} ${!selectedTableName ? 'bg-gray-50' : ''}`}
          style={selectStyle}
        >
          <option value="">
            {!selectedTableName ? 'Select table first' : 'Select column'}
          </option>
          {availableColumns.map(col => (
            <option key={col} value={col}>
              {col}
            </option>
          ))}
        </select>
      </div>

      {/* Show current selection or suggestion */}
      {(value || (showSuggestion && suggestionText)) && (
        <div className="text-xs text-gray-500 flex items-center">
          {value && (
            <span className="bg-gray-100 px-2 py-1 rounded">
              Selected: {value}
            </span>
          )}
          {showSuggestion && suggestionText && !value && (
            <span className="text-primary flex items-center">
              <Lightbulb className="h-3 w-3 mr-1" />
              Suggested: {suggestionText}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const RelationshipManager = ({ 
  table, 
  tables, 
  onSave,
  onCancel,
  isLoading = false 
}) => {
  // Custom select styling to be applied consistently.
  // This creates a custom dropdown arrow that looks the same across all browsers.
  const selectClassName = "w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:border-primary bg-white text-gray-700 appearance-none cursor-pointer hover:border-gray-400 transition-colors bg-no-repeat";
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.5rem center',
    backgroundSize: '1.5em 1.5em'
  };

  // Initialize state from existing data
  const [primaryKey, setPrimaryKey] = useState(
    table.primaryKey || ''
  );
  const [isCompositePK, setIsCompositePK] = useState(
    Array.isArray(table.primaryKey)
  );
  const [compositePKColumns, setCompositePKColumns] = useState(
    Array.isArray(table.primaryKey) ? table.primaryKey : []
  );
  const [foreignKeys, setForeignKeys] = useState(
    (table.foreignKeys || []).map(fk => {
      // Normalize to new format for internal use
      if (fk.column) {
        // Old format - convert to new
        return {
          columns: [fk.column],
          references: {
            table: fk.references.split('.')[0],
            columns: [fk.references.split('.')[1]]
          },
          type: fk.type,
          isComposite: false
        };
      }
      // Already in new format
      return {
        ...fk,
        isComposite: fk.columns?.length > 1 || false
      };
    })
  );
  const [errors, setErrors] = useState({});
  const [showImportExport, setShowImportExport] = useState(false);

  // Get available columns for this table
  const availableColumns = table.schema?.map(field => field.name) || [];

  // Get all tables except current one for foreign key references
  // Filter based on table type - main tables can only reference main tables, MVs can only reference MVs
  // This ensures proper data governance and prevents cross-boundary references
  const availableTables = tables.filter(t => 
    t.id !== table.id && 
    t.isMaterializedView === table.isMaterializedView
  );

  // Function to find suggested reference based on column name
  const findSuggestedReference = (columnName) => {
    if (!columnName) return '';

    // Clean column name (remove common suffixes like _id, _ID)
    const cleanedName = columnName.toLowerCase().replace(/_id$/i, '');
    
    // Try to find matching references (only in available tables)
    for (const refTable of availableTables) {
      // Check if column name contains table name
      if (columnName.toLowerCase().includes(refTable.name.toLowerCase()) ||
          cleanedName === refTable.name.toLowerCase()) {
        
        // Look for exact match first
        const exactMatch = refTable.schema?.find(
          field => field.name.toLowerCase() === columnName.toLowerCase()
        );
        if (exactMatch) {
          return `${refTable.name}.${exactMatch.name}`;
        }

        // Look for id column in the suggested table
        const idColumn = refTable.schema?.find(
          field => field.name.toLowerCase() === 'id' || 
                   field.name.toLowerCase() === `${refTable.name.toLowerCase()}_id`
        );
        if (idColumn) {
          return `${refTable.name}.${idColumn.name}`;
        }

        // Look for primary key in the suggested table
        if (refTable.primaryKey) {
          if (Array.isArray(refTable.primaryKey)) {
            // For composite keys, don't auto-suggest
            continue;
          } else {
            return `${refTable.name}.${refTable.primaryKey}`;
          }
        }
      }

      // Check for exact column name match in any table
      const exactColumnMatch = refTable.schema?.find(
        field => field.name.toLowerCase() === columnName.toLowerCase()
      );
      if (exactColumnMatch) {
        return `${refTable.name}.${exactColumnMatch.name}`;
      }
    }

    return '';
  };

  const handlePrimaryKeyTypeChange = (isComposite) => {
    setIsCompositePK(isComposite);
    if (!isComposite) {
      setCompositePKColumns([]);
    } else {
      setPrimaryKey('');
    }
    setErrors({});
  };

  const handleAddCompositePKColumn = () => {
    setCompositePKColumns([...compositePKColumns, '']);
  };

  const handleRemoveCompositePKColumn = (index) => {
    const newColumns = compositePKColumns.filter((_, i) => i !== index);
    setCompositePKColumns(newColumns);
  };

  const handleCompositePKColumnChange = (index, value) => {
    const newColumns = [...compositePKColumns];
    newColumns[index] = value;
    setCompositePKColumns(newColumns);
  };

  const handleAddForeignKey = () => {
    setForeignKeys([
      ...foreignKeys,
      {
        columns: [''],
        references: {
          table: '',
          columns: ['']
        },
        type: 'n:1',
        isComposite: false
      }
    ]);
  };

  const handleRemoveForeignKey = (index) => {
    const newFKs = foreignKeys.filter((_, i) => i !== index);
    setForeignKeys(newFKs);
  };

  const handleForeignKeyChange = (index, field, value, columnIndex = 0) => {
    const newFKs = [...foreignKeys];
    const fk = newFKs[index];
    
    if (field === 'column') {
      // Single column change
      fk.columns[columnIndex] = value;
      
      // Try to suggest reference for first column
      if (columnIndex === 0 && value && !fk.isComposite) {
        const suggestedRef = findSuggestedReference(value);
        if (suggestedRef && !fk.references.table) {
          const [table, column] = suggestedRef.split('.');
          fk.references = {
            table: table,
            columns: [column]
          };
        }
      }
    } else if (field === 'referenceTable') {
      fk.references.table = value;
      // Reset columns when table changes
      fk.references.columns = fk.references.columns.map(() => '');
    } else if (field === 'referenceColumn') {
      fk.references.columns[columnIndex] = value;
    } else if (field === 'type') {
      fk.type = value;
    } else if (field === 'isComposite') {
      fk.isComposite = value;
      if (!value) {
        // Converting to single - keep only first column
        fk.columns = [fk.columns[0] || ''];
        fk.references.columns = [fk.references.columns[0] || ''];
      }
    }
    
    setForeignKeys(newFKs);
  };

  const handleAddFKColumn = (index) => {
    const newFKs = [...foreignKeys];
    newFKs[index].columns.push('');
    newFKs[index].references.columns.push('');
    setForeignKeys(newFKs);
  };
  
  const handleRemoveFKColumn = (fkIndex, columnIndex) => {
    const newFKs = [...foreignKeys];
    newFKs[fkIndex].columns = newFKs[fkIndex].columns.filter((_, i) => i !== columnIndex);
    newFKs[fkIndex].references.columns = newFKs[fkIndex].references.columns.filter((_, i) => i !== columnIndex);
    setForeignKeys(newFKs);
  };

  const validate = () => {
    const newErrors = {};

    // Validate primary key
    if (isCompositePK) {
      if (compositePKColumns.length === 0) {
        newErrors.primaryKey = 'At least one column is required for composite primary key';
      } else if (compositePKColumns.some(col => !col)) {
        newErrors.primaryKey = 'All composite key columns must be selected';
      } else if (new Set(compositePKColumns).size !== compositePKColumns.length) {
        newErrors.primaryKey = 'Composite key columns must be unique';
      }
    }

    // Validate foreign keys
    foreignKeys.forEach((fk, index) => {
      const hasColumns = fk.columns.some(col => col);
      const hasReferences = fk.references.table && fk.references.columns.some(col => col);
      
      if (hasColumns && !hasReferences) {
        newErrors[`fk_${index}`] = 'Reference is required';
      }
      if (!hasColumns && hasReferences) {
        newErrors[`fk_${index}`] = 'Column is required';
      }
      
      // Check column count matches
      const filledColumns = fk.columns.filter(col => col).length;
      const filledRefColumns = fk.references.columns.filter(col => col).length;
      if (filledColumns > 0 && filledRefColumns > 0 && filledColumns !== filledRefColumns) {
        newErrors[`fk_${index}`] = 'Number of columns must match number of referenced columns';
      }
      
      // Check for empty columns in between
      if (fk.columns.some((col, i) => !col && i < fk.columns.length - 1 && fk.columns.slice(i + 1).some(c => c))) {
        newErrors[`fk_${index}`] = 'Cannot have empty columns in between';
      }
    });

    // Check for duplicate foreign key columns (flattened for all FKs)
    const allFkColumns = foreignKeys.flatMap(fk => fk.columns.filter(col => col));
    if (new Set(allFkColumns).size !== allFkColumns.length) {
      newErrors.foreignKeys = 'Each column can only have one foreign key reference';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }
  
    const relationshipData = {
      primaryKey: isCompositePK 
        ? compositePKColumns.filter(col => col)
        : (primaryKey || null),
      foreignKeys: foreignKeys
        .filter(fk => fk.columns.some(col => col) && fk.references.table)
        .map(fk => {
          const filledColumns = fk.columns.filter(col => col);
          const filledRefColumns = fk.references.columns.filter(col => col);
          
          // Convert back to simple format if single column
          if (filledColumns.length === 1) {
            return {
              column: filledColumns[0],
              references: `${fk.references.table}.${filledRefColumns[0]}`,
              type: fk.type
            };
          }
          
          // Keep composite format
          return {
            columns: filledColumns,
            references: {
              table: fk.references.table,
              columns: filledRefColumns
            },
            type: fk.type
          };
        })
    };
  
    onSave(relationshipData);
  };

  return (
    <div className="space-y-6">
      {/* Import/Export Section */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowImportExport(!showImportExport)}
          className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
        >
          <FileJson className="h-4 w-4 mr-1.5" />
          Import/Export
        </button>
      </div>

      {showImportExport && (
        <div className="border border-gray-200 rounded-md p-4 mb-4">
          <RelationshipImportExport
            currentData={{
              primaryKey: isCompositePK ? compositePKColumns.filter(col => col) : primaryKey || null,
              foreignKeys: foreignKeys
            }}
            table={table}
            availableTables={availableTables}
            onImport={(data) => {
              // Apply imported primary key
              if (Array.isArray(data.primaryKey)) {
                setIsCompositePK(true);
                setCompositePKColumns(data.primaryKey);
                setPrimaryKey('');
              } else {
                setIsCompositePK(false);
                setPrimaryKey(data.primaryKey || '');
                setCompositePKColumns([]);
              }
              
              // Apply imported foreign keys
              setForeignKeys(data.foreignKeys || []);
              
              // Clear any errors and close import/export
              setErrors({});
              setShowImportExport(false);
            }}
            onClose={() => setShowImportExport(false)}
          />
        </div>
      )}

      {/* Show info message if no tables available for references */}
      {availableTables.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-700">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">No compatible tables for foreign key references</p>
              <p className="mt-1 text-blue-600">
                {table.isMaterializedView 
                  ? "Materialized views can only reference other materialized views."
                  : "Main tables can only reference other main tables."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Primary Key Section */}
      <div>
        <div className="flex items-center mb-3">
          <h3 className="text-base font-medium text-gray-900 flex items-center">
            <Key className="h-4 w-4 mr-2" />
            Primary Key
          </h3>
          <div className="ml-2 group relative inline-block">
            <Info className="h-4 w-4 text-gray-400 cursor-help" />
            <div className="absolute left-6 top-0 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity z-10 whitespace-nowrap pointer-events-none">
              Uniquely identifies each row in this table
            </div>
          </div>
        </div>
        
        <div className="space-y-3">
          {/* Primary Key Type Selection */}
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="pkType"
                checked={!isCompositePK}
                onChange={() => handlePrimaryKeyTypeChange(false)}
                className="form-radio text-primary"
              />
              <span className="ml-2 text-sm">Single Column</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="pkType"
                checked={isCompositePK}
                onChange={() => handlePrimaryKeyTypeChange(true)}
                className="form-radio text-primary"
              />
              <span className="ml-2 text-sm">Composite (Multiple Columns)</span>
            </label>
            <div className="ml-2 group relative inline-block">
              <Info className="h-3 w-3 text-gray-400 cursor-help" />
              <div className="absolute left-5 top-0 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity z-10 w-48 pointer-events-none">
                Composite: Use multiple columns together to create a unique identifier
              </div>
            </div>
          </div>

          {/* Single Primary Key */}
          {!isCompositePK && (
            <div className="max-w-md">
              <select
                value={primaryKey}
                onChange={(e) => setPrimaryKey(e.target.value)}
                className={selectClassName}
                style={selectStyle}
              >
                <option value="">No Primary Key</option>
                {availableColumns.map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
          )}

          {/* Composite Primary Key */}
          {isCompositePK && (
            <div className="space-y-2 max-w-lg">
              {compositePKColumns.map((col, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <select
                    value={col}
                    onChange={(e) => handleCompositePKColumnChange(index, e.target.value)}
                    className={`${selectClassName} flex-1`}
                    style={selectStyle}
                  >
                    <option value="">Select column</option>
                    {availableColumns.map(availCol => (
                      <option 
                        key={availCol} 
                        value={availCol}
                        disabled={compositePKColumns.includes(availCol) && availCol !== col}
                      >
                        {availCol}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleRemoveCompositePKColumn(index)}
                    className="p-2 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={handleAddCompositePKColumn}
                className="flex items-center px-3 py-1.5 text-sm text-primary hover:text-primary/80"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Column
              </button>
            </div>
          )}

          {errors.primaryKey && (
            <div className="flex items-center text-red-500 text-sm">
              <AlertCircle className="h-4 w-4 mr-1" />
              {errors.primaryKey}
            </div>
          )}
        </div>
      </div>

      {/* Foreign Keys Section */}
      <div>
        <div className="flex items-center mb-3">
          <h3 className="text-base font-medium text-gray-900 flex items-center">
            <Link className="h-4 w-4 mr-2" />
            Foreign Keys
            {availableTables.length > 0 && (
              <span className="ml-2 text-xs font-normal text-gray-500">
                ({table.isMaterializedView ? 'Materialized View' : 'Main Table'} references only)
              </span>
            )}
          </h3>
          <div className="ml-2 group relative inline-block">
            <Info className="h-4 w-4 text-gray-400 cursor-help" />
            <div className="absolute left-6 top-0 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity z-10 whitespace-nowrap pointer-events-none">
              Links records in this table to records in other tables
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {foreignKeys.length === 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-600">
              <div className="flex items-start">
                <Lightbulb className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5 text-gray-400" />
                <div>
                  <p className="font-medium">When do you need foreign keys?</p>
                  <p className="mt-1">Add them when this table references data from other tables (like customer ID, product ID, etc.)</p>
                </div>
              </div>
            </div>
          )}
          
          {foreignKeys.map((fk, index) => (
          <div key={index} className="border border-gray-200 rounded-md p-4 space-y-4">
            {/* Composite FK Toggle */}
            <div className="flex items-center space-x-3 pb-2 mb-3 border-b border-gray-100">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  checked={fk.isComposite}
                  onChange={(e) => handleForeignKeyChange(index, 'isComposite', e.target.checked)}
                  className="form-checkbox text-primary h-4 w-4"
                />
                <span className="ml-2 text-sm font-medium">Composite Foreign Key</span>
              </label>
              <span className="text-xs text-gray-500">
                {fk.isComposite ? 'Multiple columns reference multiple columns' : 'Single column reference'}
              </span>
            </div>

            {/* Column Mappings */}
            <div className="space-y-2">
              {/* Headers - clearer and more descriptive */}
              {fk.columns.length > 0 && (
                <div className="grid grid-cols-2 gap-4 mb-1">
                  <div className="text-sm font-medium text-gray-700">
                    Column in this table
                  </div>
                  <div className="text-sm font-medium text-gray-700">
                    References (Table.Column)
                  </div>
                </div>
              )}
              
              {fk.columns.map((col, colIndex) => (
                <div key={colIndex} className="grid grid-cols-2 gap-4 items-center">
                  {/* Source Column */}
                  <div className="flex items-center space-x-2">
                    <div className="flex-1">
                      <select
                        value={col}
                        onChange={(e) => handleForeignKeyChange(index, 'column', e.target.value, colIndex)}
                        className={`${selectClassName} w-full`}
                        style={selectStyle}
                      >
                        <option value="">Select column</option>
                        {availableColumns.map(availCol => (
                          <option 
                            key={availCol} 
                            value={availCol}
                            disabled={foreignKeys.some((otherFk, i) => 
                              i !== index && otherFk.columns.includes(availCol)
                            ) || (fk.columns.includes(availCol) && fk.columns.indexOf(availCol) !== colIndex)}
                          >
                            {availCol}
                          </option>
                        ))}
                      </select>
                    </div>
                    {fk.isComposite && colIndex > 0 && (
                      <button
                        onClick={() => handleRemoveFKColumn(index, colIndex)}
                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                        title="Remove column"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Improved Referenced Table & Column selectors (side-by-side) */}
                  <div className="flex items-center space-x-2 w-full">
                    <div className="flex-1 min-w-0">
                      {/* Table selection (only for first column) */}
                      {colIndex === 0 ? (
                        <select
                          value={fk.references.table}
                          onChange={(e) => handleForeignKeyChange(index, 'referenceTable', e.target.value)}
                          className={`${selectClassName} w-full`}
                          style={selectStyle}
                          disabled={availableTables.length === 0}
                        >
                          <option value="">Select table</option>
                          {availableTables.map(table => (
                            <option key={table.id} value={table.name}>
                              {table.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="w-full px-3 py-2 text-sm border border-gray-200 bg-gray-50 text-gray-500 rounded-md truncate" title={fk.references.table}>
                          {fk.references.table}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      {/* Column selection */}
                      <select
                        value={fk.references.columns[colIndex] || ''}
                        onChange={(e) => handleForeignKeyChange(index, 'referenceColumn', e.target.value, colIndex)}
                        className={`${selectClassName} w-full ${!fk.references.table ? 'bg-gray-50' : ''}`}
                        style={selectStyle}
                        disabled={!fk.references.table}
                      >
                        <option value="">
                          {!fk.references.table ? 'Select table first' : 'Select column'}
                        </option>
                        {fk.references.table && availableTables
                          .find(t => t.name === fk.references.table)?.schema
                          ?.map(field => field.name)
                          .map(refCol => (
                            <option key={refCol} value={refCol}>
                              {refCol}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Add Column Button for Composite FK */}
              {fk.isComposite && (
                <button
                  onClick={() => handleAddFKColumn(index)}
                  className="flex items-center px-3 py-1.5 text-sm text-primary hover:text-primary/80 mt-2"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Column Mapping
                </button>
              )}
            </div>

            {/* Relationship Type - only show once */}
            <div className="pt-2 mt-3 border-t border-gray-100">
              <div className="flex items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Relationship Type
                </label>
                <div className="ml-2 group relative">
                  <Info className="h-3 w-3 text-gray-400 cursor-help" />
                  <div className="absolute left-0 top-4 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity z-10 w-48">
                    How records relate between tables
                  </div>
                </div>
              </div>
              <div className="max-w-md">
                <select
                  value={fk.type}
                  onChange={(e) => handleForeignKeyChange(index, 'type', e.target.value)}
                  className={`${selectClassName} w-full`}
                  style={selectStyle}
                >
                  <option value="n:1">Many to One</option>
                  <option value="1:1">One to One</option>
                  <option value="1:n">One to Many</option>
                  <option value="n:n">Many to Many</option>
                </select>
              </div>
              <div className="mt-1 text-xs text-gray-500">
                {fk.type === 'n:1' && "Example: Many orders → one customer"}
                {fk.type === '1:1' && "Example: One user → one profile"}
                {fk.type === '1:n' && "Example: One customer → many orders"}
                {fk.type === 'n:n' && "Example: Students ↔ courses (requires junction table)"}
              </div>
            </div>

            <div className="flex justify-end pt-2 mt-2">
              <button
                onClick={() => handleRemoveForeignKey(index)}
                className="text-red-500 hover:text-red-700 text-sm flex items-center hover:bg-red-50 px-2 py-1 rounded transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Remove Foreign Key
              </button>
            </div>

            {errors[`fk_${index}`] && (
              <div className="flex items-center text-red-500 text-sm">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors[`fk_${index}`]}
              </div>
            )}
          </div>
        ))}

          <button
            onClick={handleAddForeignKey}
            disabled={availableTables.length === 0}
            className={`flex items-center px-4 py-2 text-sm rounded-md ${
              availableTables.length === 0 
                ? 'text-gray-400 border border-gray-300 cursor-not-allowed bg-gray-50' 
                : 'text-primary border border-primary hover:bg-primary/5'
            }`}
            title={availableTables.length === 0 ? 'No compatible tables available for references' : ''}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Foreign Key
          </button>

          {errors.foreignKeys && (
            <div className="flex items-center text-red-500 text-sm">
              <AlertCircle className="h-4 w-4 mr-1" />
              {errors.foreignKeys}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="px-4 py-2 text-sm text-white bg-primary rounded-md hover:bg-primary/90 disabled:bg-gray-400"
        >
          {isLoading ? 'Saving...' : 'Save Relationships'}
        </button>
      </div>
    </div>
  );
};

export default RelationshipManager;